import { useCallback, useState } from 'react';
import {
  ClientSessionIndividual,
  useGetClientSessionLazyQuery,
  ClientSessionReviewStatus,
} from '../../../../generated/graphql';
import { SessionModalMode, SessionModalType } from '../types';
import { logger } from '../../../../lib/logger';
import { customToast } from '../../../components/ToastAlert/customToast';

const EXPECTED_SESSION_ERROR = /Token is expired|TokenInvalid/i;

type SessionFetchError = {
  message?: string;
  graphQLErrors?: ReadonlyArray<{
    message?: string;
    extensions?: { code?: string };
  }>;
};

/**
 * Whether a failed client-session fetch represents an expected, recoverable
 * condition (the session token expired mid-flight) rather than a real bug.
 * The hook handles both by showing a user-facing toast, but expected errors
 * are downgraded to a warning so they don't create Sentry noise (WEB-48).
 */
export const isExpectedSessionFetchError = (
  error: SessionFetchError | undefined,
): boolean => {
  if (!error) return false;
  if (error.message && EXPECTED_SESSION_ERROR.test(error.message)) return true;
  return Boolean(
    error.graphQLErrors?.some(
      (e) =>
        EXPECTED_SESSION_ERROR.test(e.message ?? '') ||
        EXPECTED_SESSION_ERROR.test(e.extensions?.code ?? ''),
    ),
  );
};

export const useSessionModal = () => {
  const [modalType, setModalType] = useState<SessionModalType>(null);
  const [selectedSession, setSelectedSession] =
    useState<ClientSessionIndividual | null>(null);
  const [getClientSession] = useGetClientSessionLazyQuery();

  const handleModalOpen = useCallback(
    async (sessionId: string) => {
      try {
        // Fetch session data using the ID
        const { data, error } = await getClientSession({
          variables: { id: sessionId },
        });

        if (error || !data?.clientSession) {
          // A token that expired mid-session (TokenInvalid / "Token is expired")
          // is expected and already handled below — we surface a user-facing
          // toast and recover. Logging it as an error-level Sentry event creates
          // false noise (WEB-48), so downgrade to a warning breadcrumb unless the
          // failure looks genuinely unexpected.
          if (isExpectedSessionFetchError(error)) {
            logger.warn(
              'Session fetch skipped — session token expired:',
              error,
            );
          } else {
            logger.error('Error fetching session:', error);
          }
          customToast.error('Unable to load session details');
          return;
        }

        const fetchedSession = data.clientSession as ClientSessionIndividual;

        // Determine the modal mode based on the session's status
        const modalMode =
          fetchedSession.reviewStatus ===
          ClientSessionReviewStatus.PendingReview
            ? SessionModalMode.REVIEW
            : SessionModalMode.UPDATE;

        setSelectedSession(fetchedSession);
        setModalType(modalMode);
      } catch (err) {
        logger.error('Error in handleModalOpen:', err);
        customToast.error('Unable to load session details');
      }
    },
    [getClientSession],
  );

  const handleModalClose = useCallback(() => {
    setSelectedSession(null);
    setModalType(null);
  }, []);

  const openCreateModal = useCallback(() => {
    setSelectedSession(null);
    setModalType(SessionModalMode.CREATE);
  }, []);

  return {
    modalType,
    selectedSession,
    openModal: handleModalOpen,
    openCreateModal,
    closeModal: handleModalClose,
  };
};
